import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IncidentMapping } from '../models/Mapping';
import { useMappings, UseMappingsFilters } from './useMappings';
import { useQueryParams } from '@shared/hooks/useQueryParams';

type SortingState = { field: string; direction: 'asc' | 'desc' } | null;

const INITIAL_SELECTED_COLUMNS = ['id', 'courier', 'external_type', 'internal_type', 'status'] as const;

const FIXED_COLUMNS = ['id'] as const;

const FIELD_TO_API_SORT: Record<string, string> = {
  id: 'id',
  status: 'status',
};

export const useMappingsMain = () => {
  const {
    params: queryParams,
    updateParam,
    updateParams,
  } = useQueryParams({
    sortBy: { type: 'string', default: 'id' as string },
    sortDirection: { type: 'string', default: 'desc' as string },
    courier_id: { type: 'number', default: undefined },
    status: { type: 'string', default: undefined },
  });
  const [sorting, setSorting] = useState<SortingState>(() => {
    const sortField = queryParams.sortBy || 'id';
    const direction = queryParams.sortDirection === 'asc' ? 'asc' : 'desc';
    return {
      field: sortField,
      direction,
    };
  });
  const [selectedColumns, setSelectedColumns] = useState<string[]>([...INITIAL_SELECTED_COLUMNS]);
  const [cachedMappings, setCachedMappings] = useState<IncidentMapping[]>([]);
  const [cachedTotal, setCachedTotal] = useState(0);

  useEffect(() => {
    const sortField = queryParams.sortBy || 'id';
    const direction = queryParams.sortDirection === 'asc' ? 'asc' : 'desc';

    setSorting((prev) => {
      if (prev && prev.field === sortField && prev.direction === direction) {
        return prev;
      }

      return {
        field: sortField,
        direction,
      };
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  const processedFilters = useMemo(() => {
    const apiField = sorting?.field ? FIELD_TO_API_SORT[sorting.field] || sorting.field : 'id';
    const apiDirection = sorting?.direction ?? 'desc';

    return {
      sortBy: apiField,
      sortDirection: apiDirection,
    };
  }, [sorting]);

  const queryFilters = useMemo(() => {
    const baseFilters: UseMappingsFilters = {
      ...processedFilters,
      courier_id: queryParams.courier_id,
      status: queryParams.status as 'mapped' | 'unmapped' | undefined,
    };

    return baseFilters;
  }, [processedFilters, queryParams.courier_id, queryParams.status]);

  const {
    mappings: fetchedMappings,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total: fetchedTotal,
  } = useMappings(queryFilters);

  useEffect(() => {
    if (fetchedMappings.length > 0) {
      setCachedMappings(fetchedMappings);
      setCachedTotal(fetchedTotal);
    }
  }, [fetchedMappings.length, fetchedTotal]);

  const handleColumnsChange = useCallback((columns: string[]) => {
    setSelectedColumns(columns);
  }, []);

  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      setSorting(newSorting);
      if (newSorting && newSorting.field) {
        updateParams({
          sortBy: newSorting.field,
          sortDirection: newSorting.direction,
        });
      } else {
        updateParams({
          sortBy: '',
          sortDirection: '',
        });
      }
    },
    [updateParams],
  );

  // 🎯 Detectar si hay filtros activos
  const hasActiveFilters = useMemo(
    () => queryParams.courier_id !== undefined || queryParams.status !== undefined,
    [queryParams.courier_id, queryParams.status],
  );

  const shouldUseCachedData = !hasActiveFilters && fetchedMappings.length === 0 && cachedTotal > 0;

  const mappings = shouldUseCachedData ? cachedMappings : fetchedMappings;
  const total = shouldUseCachedData ? cachedTotal : fetchedTotal;

  const mappingsViewProps = useMemo(
    () => ({
      mappings,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
    }),
    [mappings, fetchNextPage, hasNextPage, isFetchingNextPage, total],
  );

  const exportColumns = useMemo(() => selectedColumns.filter(Boolean) as string[], [selectedColumns]);

  return {
    mappings,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
    sorting,
    selectedColumns,
    mappingsViewProps,
    exportColumns,
    hasActiveFilters,
    fixedColumns: [...FIXED_COLUMNS],
    handleColumnsChange,
    handleSortingChange,
    // Filtros específicos de mappings
    courierId: queryParams.courier_id,
    status: queryParams.status as 'mapped' | 'unmapped' | undefined,
    handleCourierChange: useCallback(
      (courierId: number | undefined) => {
        updateParam('courier_id', courierId);
      },
      [updateParam],
    ),
    handleStatusChange: useCallback(
      (status: 'mapped' | 'unmapped' | undefined) => {
        updateParam('status', status);
      },
      [updateParam],
    ),
  };
};
